import { FormInput } from "@general-backoffice/core";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, CardBody, CardHeader } from "reactstrap";

import getCurrencySymbol from "../../../../../utils/getCurrencySymbol";

interface PerformanceDataPoint {
  timestamp: number;
  profit?: number | null;
  absoluteProfit?: number | null;
  drawdown?: number | null;
}

interface PeriodStatisticsCardProps {
  data?: PerformanceDataPoint[];
  currency?: string;
}

/**
 * Groups snapshots by calendar day (YYYY-MM-DD), computes each day's mean drawdown,
 * then averages across days. This avoids skewing toward recent data which has
 * higher snapshot density.
 */
function computeDailyResampledAvgDrawdown(snapshots: PerformanceDataPoint[]): number {
  const dayBuckets = new Map<string, number[]>();

  for (const s of snapshots) {
    if (s.drawdown == null) continue;
    const dayKey = moment(s.timestamp).format("YYYY-MM-DD");
    let bucket = dayBuckets.get(dayKey);
    if (!bucket) {
      bucket = [];
      dayBuckets.set(dayKey, bucket);
    }
    bucket.push(s.drawdown);
  }

  if (dayBuckets.size === 0) return 0;

  let totalDayMeans = 0;
  for (const values of dayBuckets.values()) {
    const dayMean = values.reduce((sum, v) => sum + v, 0) / values.length;
    totalDayMeans += dayMean;
  }

  return totalDayMeans / dayBuckets.size;
}

const PeriodStatisticsCard: React.FC<PeriodStatisticsCardProps> = ({ data = [], currency }) => {
  const { t } = useTranslation("dashboard", { keyPrefix: "periodStatistics" });
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);

  const symbol = useMemo(() => getCurrencySymbol(currency), [currency]);

  // Set of YYYY-MM-DD strings that have at least one snapshot
  const snapshotDays = useMemo(() => {
    const days = new Set<string>();
    for (const p of data) {
      days.add(moment(p.timestamp).format("YYYY-MM-DD"));
    }
    return days;
  }, [data]);

  const stats = useMemo(() => {
    if (!startDate || !endDate || data.length === 0) return null;

    const startMs = moment(startDate).startOf("day").valueOf();
    const endMs = moment(endDate).endOf("day").valueOf();

    const filtered = data.filter((p) => p.timestamp >= startMs && p.timestamp <= endMs);

    if (filtered.length < 2) return null;

    const first = filtered[0];
    const last = filtered[filtered.length - 1];

    // Compound return for the period: (1+cumEnd)/(1+cumStart) - 1
    const lastProfit = last.profit ?? 0;
    const firstProfit = first.profit ?? 0;
    const profitPercent = ((1 + lastProfit / 100) / (1 + firstProfit / 100) - 1) * 100;
    const absoluteProfit = (last.absoluteProfit ?? 0) - (first.absoluteProfit ?? 0);
    const avgDrawdown = computeDailyResampledAvgDrawdown(filtered);
    const maxDrawdown = Math.max(...filtered.map((s) => s.drawdown ?? 0));

    const days = moment(last.timestamp).diff(moment(first.timestamp), "days");

    return { profitPercent, absoluteProfit, avgDrawdown, maxDrawdown, snapshotCount: filtered.length, days };
  }, [data, startDate, endDate]);

  const handleReset = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const hasDateRange = !!startDate || !!endDate;

  return (
    <Card className="mb-3" style={{ overflow: "visible" }}>
      <CardHeader className="py-3 bg-transparent d-flex justify-content-between align-items-center">
        <h6 className="mb-0 text-uppercase text-muted" style={{ fontSize: "0.7rem" }}>
          <i className="fas fa-calculator mr-2" aria-hidden="true" />
          {t("title")}
        </h6>
        {hasDateRange && (
          <Button color="warning" size="sm" onClick={handleReset}>
            <i className="fas fa-broom mr-1" aria-hidden="true" />
            {t("reset")}
          </Button>
        )}
      </CardHeader>
      <CardBody className="pt-0 pb-3" style={{ overflow: "visible" }}>
        <div className="d-flex" style={{ gap: "1rem" }}>
          <div style={{ flex: 1 }}>
            <FormInput.DatePicker
              label={t("startDate")}
              key={startDate}
              value={startDate ? moment(startDate) : undefined}
              onChange={(d: moment.Moment | string) => {
                const selected = d && typeof d !== "string" ? d.startOf("day") : undefined;
                setStartDate(selected ? selected.toISOString() : undefined);
                if (selected && !endDate) {
                  const oneMonthLater = selected.clone().add(1, "month");
                  // Find the latest snapshot day that is <= oneMonthLater
                  const sortedDays = Array.from(snapshotDays).sort();
                  const selectedKey = selected.format("YYYY-MM-DD");
                  const candidates = sortedDays.filter((d) => d >= selectedKey && d <= oneMonthLater.format("YYYY-MM-DD"));
                  const autoEnd = candidates.length > 0 ? candidates[candidates.length - 1] : sortedDays[sortedDays.length - 1];
                  if (autoEnd && autoEnd >= selectedKey) {
                    setEndDate(moment(autoEnd).endOf("day").toISOString());
                  }
                }
              }}
              timeFormat={false}
              dateFormat="DD-MM-YYYY"
              isValidDate={(d: moment.Moment) => {
                if (!snapshotDays.has(d.format("YYYY-MM-DD"))) return false;
                if (endDate && d.isAfter(endDate)) return false;
                return true;
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <FormInput.DatePicker
              label={t("endDate")}
              key={endDate}
              value={endDate ? moment(endDate) : undefined}
              onChange={(d: moment.Moment | string) => {
                setEndDate(d && typeof d !== "string" ? d.endOf("day").toISOString() : undefined);
              }}
              timeFormat={false}
              dateFormat="DD-MM-YYYY"
              isValidDate={(d: moment.Moment) => {
                if (!snapshotDays.has(d.format("YYYY-MM-DD"))) return false;
                if (startDate && d.isBefore(startDate)) return false;
                return true;
              }}
            />
          </div>
        </div>

        {stats ? (
          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center py-1 stat-row border-bottom">
              <small className="text-muted">{t("period")}</small>
              <span className="font-weight-bold">{stats.days} days</span>
            </div>
            <div className="d-flex justify-content-between align-items-center py-1 stat-row border-bottom">
              <small className="text-muted">{t("profitPercent")}</small>
              <span className={`font-weight-bold ${stats.profitPercent < 0 ? "metric-negative" : "metric-positive"}`}>
                {stats.profitPercent < 0 ? "" : "+"}
                {stats.profitPercent.toFixed(2)}%
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center py-1 stat-row border-bottom">
              <small className="text-muted">{t("absoluteProfit")}</small>
              <span className={`font-weight-bold ${stats.absoluteProfit < 0 ? "metric-negative" : "metric-positive"}`}>
                {stats.absoluteProfit < 0 ? "-" : "+"}
                {Math.abs(stats.absoluteProfit).toFixed(2)} {symbol}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center py-1 stat-row border-bottom">
              <small className="text-muted">{t("avgDrawdown")}</small>
              <span
                className={`font-weight-bold ${
                  stats.avgDrawdown > 30 ? "metric-negative" : stats.avgDrawdown > 15 ? "metric-warning" : ""
                }`}
              >
                {stats.avgDrawdown.toFixed(2)}%
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center py-1 stat-row border-bottom">
              <small className="text-muted">{t("maxDrawdown")}</small>
              <span
                className={`font-weight-bold ${
                  stats.maxDrawdown > 30 ? "metric-negative" : stats.maxDrawdown > 15 ? "metric-warning" : ""
                }`}
              >
                {stats.maxDrawdown.toFixed(2)}%
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center py-1 stat-row">
              <small className="text-muted">{t("snapshots")}</small>
              <span className="font-weight-bold">{stats.snapshotCount}</span>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted py-3">
            <small>{hasDateRange ? "Not enough data in selected range (need at least 2 snapshots)" : "Select a date range to view period statistics"}</small>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default PeriodStatisticsCard;
